import { Download, MoreVertical, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import loadingBlueIconUrl from "@/assets/svg/loading-blue.svg?url";
import { getProjectFinalAudioUrl } from "@/api/client";
import type { ProjectSummary, ProjectWorkflowStatus } from "@/api/client";

const WORKFLOW_LABELS: Record<ProjectWorkflowStatus, string> = {
  termine: "Terminé",
  en_cours: "En cours",
  brouillon: "Brouillon"
};

function workflowBadgeVariant(status: ProjectWorkflowStatus | undefined): "success" | "warning" | "muted" {
  if (status === "termine") return "success";
  if (status === "en_cours") return "warning";
  return "muted";
}

function formatAddedLong(value?: string) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

export type ProjectCardProps = {
  project: ProjectSummary;
  displayWf: ProjectWorkflowStatus;
  menuOpen: boolean;
  previewProject: string | null;
  previewKey: number;
  menuRef: React.RefObject<HTMLDivElement>;
  onSelect: (project: ProjectSummary) => void;
  onMenuToggle: (name: string) => void;
  onDownloadOpen: (project: ProjectSummary) => void;
  onDelete: (project: ProjectSummary) => void;
};

export function ProjectCard({
  project,
  displayWf,
  menuOpen,
  previewProject,
  previewKey,
  menuRef,
  onSelect,
  onMenuToggle,
  onDownloadOpen,
  onDelete,
}: ProjectCardProps) {
  const hasAudio = Boolean(project.final_audio?.path);
  const artifactCount = project.artifact_count ?? 0;
  const tags = project.tags ?? [];
  const addedLong = formatAddedLong(project.created_at);
  const loc = project.location?.trim();
  const desc =
    project.description_preview?.trim() ||
    "Aucune description pour l'instant — renseignez le contexte narratif dans Détails du projet.";

  return (
    <li className="flex w-full min-w-0 flex-col gap-1 sm:w-[560px]">
      <Card
        className={cn("overflow-hidden p-0 transition-shadow hover:shadow-md cursor-pointer min-h-[330px]")}
        role="button"
        tabIndex={0}
        onClick={() => void onSelect(project)}
        onKeyDown={(evt) => {
          if (evt.key === "Enter" || evt.key === " ") {
            evt.preventDefault();
            void onSelect(project);
          }
        }}
      >
        <CardContent className="flex h-full flex-col gap-3 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <Badge
                variant="outline"
                className="h-[33.6px] gap-1 rounded-[12px] border border-primary bg-[#F4F4F4] px-3 py-[5px] font-medium text-primary"
              >
                <img src={loadingBlueIconUrl} alt="" className="size-3.5 shrink-0" aria-hidden />
                {artifactCount} artefact{artifactCount > 1 ? "s" : ""}
              </Badge>
            </div>
            <div
              className="relative shrink-0"
              ref={menuOpen ? menuRef : null}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                type="button"
                variant="ghost"
                className="size-[33.6px] rounded-[10px] border border-[#C3C3C3] bg-background text-foreground hover:bg-muted/70"
                aria-expanded={menuOpen}
                aria-haspopup="true"
                aria-label="Plus d'actions"
                onClick={() => onMenuToggle(project.name)}
              >
                <MoreVertical className="size-4" />
              </Button>
              {menuOpen ? (
                <div
                  className="absolute right-0 top-full z-20 mt-2 w-[240px] overflow-hidden rounded-[8px] border border-[#E2E8F0] bg-background text-sm shadow-[0_2px_10px_rgba(0,0,0,0.10)]"
                  role="menu"
                >
                  <button
                    type="button"
                    role="menuitem"
                    className="flex h-[57px] w-full items-center gap-2 px-3 py-2 text-left text-base font-normal text-foreground hover:bg-muted"
                    onClick={() => onDownloadOpen(project)}
                  >
                    <Download className="size-4 shrink-0" />
                    Télécharger l'archive
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex h-[57px] w-full items-center gap-2 border-t border-[#E2E8F0] px-3 py-2 text-left text-[#FF1700] hover:bg-muted"
                    onClick={() => onDelete(project)}
                  >
                    <Trash2 className="size-4 shrink-0" />
                    Supprimer
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <div className="min-w-0">
            <h3 className="text-xl font-semibold leading-tight text-foreground">{project.name}</h3>
          </div>

          <p className="text-sm text-muted-foreground leading-[23px] line-clamp-4">{desc}</p>

          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant={workflowBadgeVariant(displayWf)}
              className={cn(
                "font-medium",
                displayWf === "termine" && "border border-success bg-success-muted"
              )}
            >
              {WORKFLOW_LABELS[displayWf]}
            </Badge>
            {tags.map((tag) => (
              <Badge key={tag} variant="muted" className="font-normal">
                {tag}
              </Badge>
            ))}
          </div>

          <div className="flex flex-col gap-1 text-xs text-muted-foreground">
            {loc || addedLong ? (
              <p>
                {loc ? <span>{loc}</span> : null}
                {loc && addedLong ? <span className="text-muted-foreground/80">{" • "}</span> : null}
                {addedLong ? <span>Ajouté le {addedLong}</span> : null}
              </p>
            ) : null}
          </div>

          <div
            className="-mx-4 -mb-4 mt-auto flex gap-2 rounded-b-xl border-t border-border bg-[#F4F4F4] px-4 pb-4 pt-3"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="button"
              variant="outline"
              className="h-11 flex-1 rounded-lg font-medium"
              onClick={() => void onSelect(project)}
            >
              Consulter l'archive
            </Button>
          </div>
        </CardContent>
      </Card>

      {hasAudio && previewProject === project.name ? (
        <audio
          key={`${project.name}-${previewKey}`}
          controls
          autoPlay
          className="w-full rounded-lg border border-border bg-background px-2 py-1"
          src={getProjectFinalAudioUrl(project.name)}
          onClick={(e) => e.stopPropagation()}
        />
      ) : null}
    </li>
  );
}
